<script>
    // 허브 [사용량·비용] — 이 허브에 담긴 챗들의 AI 사용량·추정 비용(LLM + 음성). **소유자 전용 화면**(콘솔 허브 빌더).
    // 서버: SERVER/module/Usage.js getHubUsage · 원장: AI_LLM_USAGE(LLM, chat-core/util/llmUsage.js) / AI_VOICE_USAGE(음성, Message.js)
    //
    // ⭐ 실사용(공개 페이지 = 학습자)과 프리뷰·점검(빌더 프리뷰·레슨 점검 도구)을 **나눠** 보인다 — 저작·시험 비용이 섞이면
    //    '학습자 한 명에 얼마'를 잘못 읽는다. 위 카드 두 장은 나란히 두고, 아래 상세(일별·챗별·용도별)는 고른 카드 쪽만 보인다.
    // ⚠️ 금액은 추정이다(각 사 공개 정가 · 서버가 내려준 고정 환율 usdKrw). 청구서와 다를 수 있다는 걸 화면에 늘 적는다.
    // ⚠️ 테두리는 스코프 CSS 로 그린다 — 콘솔은 preflight 가 꺼져 있어 tw-border 만으로는 선이 안 그려진다.
    import { PRConnect } from "@projectroom/ui-lib";
    import MiniChart from "../insights/MiniChart.svelte";

    export let botId = null;

    // chat-core 요청의 purpose 이름표(llmUsage) → 화면 말. 서버에 새 이름표를 붙이면 여기도 한 줄 더한다(없으면 이름표 그대로 보인다).
    const PURPOSE_LABEL = {
        answer: "자유 답변",
        guide: "스텝 안내·유도",
        fill: "블록 문구",
        rewrite: "언어 가드 고쳐 쓰기",
        route: "블록 고르기",
        judge: "목표 판정·채점",
        probe: "사전·사후 점검 채점",
        eval: "평가문",
        rag: "문서 검색 준비",
        review: "스텝 점검(저작)",
        index: "문서 색인(저작)",
        other: "기타",
    };
    const ENV_INFO = {
        production: { label: "실사용", desc: "공개 페이지 · 학습자" },
        preview: { label: "프리뷰·점검", desc: "빌더 프리뷰 · 점검 도구" },
    };
    const PRESETS = [
        { key: "7d", label: "7일" },
        { key: "30d", label: "30일" },
        { key: "month", label: "이번 달" },
        { key: "lastMonth", label: "지난 달" },
        { key: "custom", label: "직접" },
    ];

    let preset = "30d";
    let customFrom = "";
    let customTo = "";
    let env = "production";
    let data = null;
    let loading = false;
    let error = "";

    // 날짜는 KST 기준 YYYY-MM-DD — 서버(Usage.periodOf)도 같은 기준으로 자른다. 기기 시간대를 타지 않게 고정 오프셋으로 계산한다.
    const kstToday = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
    const shiftDay = (day, n) => {
        const d = new Date(`${day}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + n);
        return d.toISOString().slice(0, 10);
    };
    const rangeOf = (p) => {
        const today = kstToday();
        if (p === "7d") return { from: shiftDay(today, -6), to: today };
        if (p === "month") return { from: `${today.slice(0, 8)}01`, to: today };
        if (p === "lastMonth") {
            const end = shiftDay(`${today.slice(0, 8)}01`, -1);
            return { from: `${end.slice(0, 8)}01`, to: end };
        }
        if (p === "custom") return { from: customFrom, to: customTo };
        return { from: shiftDay(today, -29), to: today };
    };
    const pickPreset = (p) => {
        // [직접]으로 바꿀 때 빈 칸에서 시작하지 않게 지금 보던 기간을 채워 둔다
        if (p === "custom" && (!customFrom || !customTo)) {
            const cur = rangeOf(preset);
            customFrom = cur.from;
            customTo = cur.to;
        }
        preset = p;
    };

    let reqSeq = 0;
    const load = () => {
        if (!botId) return;
        const r = rangeOf(preset);
        if (!r.from || !r.to) return; // [직접] 입력 중
        const mine = ++reqSeq;
        loading = true;
        error = "";
        PRConnect.send("/api/Usage/getHubUsage", { botId, from: r.from, to: r.to }, (res) => {
            if (mine !== reqSeq) return; // 기간을 빨리 바꿨을 때 늦게 온 옛 응답이 새 화면을 덮지 않게
            data = res.result || null;
            loading = false;
        }, (err) => {
            if (mine !== reqSeq) return;
            error = err.header?.message || "불러오지 못했습니다.";
            loading = false;
        });
    };

    // 허브·기간이 바뀌면 다시 부른다
    $: botId, preset, customFrom, customTo, load();

    // ── 표시 형식 ──────────────────────────────────────────────
    $: rate = data?.usdKrw || 1400;
    const krwOf = (usd, r) => {
        const v = (Number(usd) || 0) * r;
        if (!v) return "₩0";
        if (v < 10) return `₩${v.toFixed(1)}`;
        return `₩${Math.round(v).toLocaleString()}`;
    };
    $: krw = (usd) => krwOf(usd, rate);
    const usdText = (v) => {
        const n = Number(v) || 0;
        return n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(4)}`;
    };
    const compactFmt = new Intl.NumberFormat("ko-KR", { notation: "compact", maximumFractionDigits: 1 });
    const compact = (n) => compactFmt.format(Number(n) || 0);
    const int = (n) => (Number(n) || 0).toLocaleString();
    $: per = (usd, count) => (count > 0 ? krwOf(usd / count, rate) : "–");
    // 응답 모델 이름의 날짜 꼬리(gpt-4.1-mini-2025-04-14)는 같은 모델이라 떼고 합친다
    const modelName = (m) => String(m || "").replace(/-\d{4}-\d{2}-\d{2}$/, "");
    const voiceLine = (b) => {
        const parts = [];
        if (b.tts.calls) parts.push(`읽어주기 ${compact(b.tts.chars)}자`);
        if (b.stt.calls) parts.push(`음성 입력 ${int(Math.round(b.stt.seconds))}초`);
        return parts.join(" · ");
    };

    // ── 파생 값 ────────────────────────────────────────────────
    $: totals = data?.totals || null;
    $: cur = totals ? totals[env] : null;
    $: unknownCost = totals ? totals.unknown.costUsd : 0;
    $: unpriced = totals ? totals.production.llm.unpriced + totals.preview.llm.unpriced + totals.unknown.llm.unpriced : 0;
    $: rows = (data?.chats || [])
        .map((c, i) => ({ chatId: c.chatId, title: c.title, b: c.env[env], order: i }))
        .sort((a, b) => b.b.costUsd - a.b.costUsd || a.order - b.order);
    $: purposes = (data?.byPurpose?.[env] || []).map((p) => ({ ...p, label: PURPOSE_LABEL[p.key] || p.key }));
    $: models = Object.values(
        (data?.byModel?.[env] || []).reduce((acc, m) => {
            const name = modelName(m.key);
            const a = acc[name] || (acc[name] = { name, calls: 0, inputTokens: 0, outputTokens: 0, costUsd: 0, unpriced: 0 });
            a.calls += m.calls;
            a.inputTokens += m.inputTokens;
            a.outputTokens += m.outputTokens;
            a.costUsd += m.costUsd;
            a.unpriced += m.unpriced;
            return acc;
        }, {}),
    ).sort((a, b) => b.costUsd - a.costUsd || b.calls - a.calls);
    $: maxPurpose = Math.max(0, ...purposes.map((p) => p.costUsd));
    $: maxModel = Math.max(0, ...models.map((m) => m.costUsd));
    // 일별 막대 — 원화(추정). MiniChart 는 단일 시리즈라 고른 환경의 LLM+음성 합만 그린다.
    $: series = (data?.daily || []).map((d) => ({
        label: d.date.slice(5).replace("-", "/"),
        value: Math.round((d[env].llm + d[env].voice) * rate * 10) / 10,
    }));
    $: empty = !!totals && totals[env].costUsd === 0 && totals[env].turns === 0;
    const pct = (v, max) => (max > 0 ? Math.max(2, Math.round((v / max) * 100)) : 0);
</script>

<div class="usage tw-font-sans tw-text-foreground">
    <div class="head">
        <div class="head-l">
            <h3>사용량·비용</h3>
            <span class="pill">LLM · 음성</span>
        </div>
        <div class="seg" role="tablist" aria-label="기간">
            {#each PRESETS as p (p.key)}
                <button type="button" role="tab" aria-selected={preset === p.key} class:on={preset === p.key} on:click={() => pickPreset(p.key)}>{p.label}</button>
            {/each}
        </div>
    </div>

    {#if preset === "custom"}
        <div class="custom">
            <input type="date" aria-label="시작일" bind:value={customFrom} max={customTo || undefined} />
            <span>~</span>
            <input type="date" aria-label="종료일" bind:value={customTo} min={customFrom || undefined} />
        </div>
    {/if}

    {#if !botId}
        <div class="empty">허브를 저장하면 사용량을 볼 수 있어요.</div>
    {:else if error}
        <div class="empty err">{error}</div>
    {:else if !data}
        <div class="empty">불러오는 중…</div>
    {:else}
        <div class="period">
            {data.period.from} ~ {data.period.to} · {data.period.days}일 · 한국 시간 기준
            {#if loading}<span class="dim">· 새로 불러오는 중…</span>{/if}
        </div>

        <!-- 환경 카드 두 장. 누르면 아래 상세가 그 환경으로 바뀐다 -->
        <div class="envs" role="tablist" aria-label="환경">
            {#each ["production", "preview"] as e (e)}
                {@const b = totals[e]}
                <!-- 안에 목록(dl)을 품어서 button 대신 div 탭으로 둔다(button 안엔 블록 요소를 못 넣는다) -->
                <div
                    role="tab"
                    tabindex="0"
                    aria-selected={env === e}
                    class="env"
                    class:on={env === e}
                    on:click={() => (env = e)}
                    on:keydown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            env = e;
                        }
                    }}
                >
                    <div class="env-h">
                        <span class="env-t">{ENV_INFO[e].label}</span>
                        <span class="env-d">{ENV_INFO[e].desc}</span>
                    </div>
                    <div class="env-n">{krw(b.costUsd)}<span class="env-usd">{usdText(b.costUsd)}</span></div>
                    <dl class="env-rows">
                        <div><dt>LLM</dt><dd>{krw(b.llm.costUsd)} <span class="dim">· {int(b.llm.calls)}회</span></dd></div>
                        <div><dt>음성</dt><dd>{krw(b.tts.costUsd + b.stt.costUsd)}{#if voiceLine(b)} <span class="dim">· {voiceLine(b)}</span>{/if}</dd></div>
                        <div><dt>대화</dt><dd>{int(b.turns)}턴 <span class="dim">· 학습자 {int(b.learners)}명</span></dd></div>
                        <div><dt>단가</dt><dd>턴당 {per(b.costUsd, b.turns)} <span class="dim">· 학습자당 {per(b.costUsd, b.learners)}</span></dd></div>
                    </dl>
                </div>
            {/each}
        </div>

        {#if unknownCost > 0}
            <div class="note">구분 없는 기록 {krw(unknownCost)} — 환경 구분이 생기기 전의 음성 기록이에요. 위 두 카드에는 넣지 않았어요.</div>
        {/if}
        {#if unpriced > 0}
            <div class="note warn">요율표에 없는 모델 호출 {int(unpriced)}회는 금액에서 빠졌어요(토큰 수는 집계돼요).</div>
        {/if}

        {#if empty}
            <div class="empty">이 기간에 {ENV_INFO[env].label} 기록이 없어요.</div>
        {:else}
            <MiniChart title="일별 비용" subtitle={`${ENV_INFO[env].label} · 원(추정)`} kind="bar" {series} />

            <!-- 챗별 — 비용 큰 순. 기록 없는 챗도 0 으로 남긴다(허브 격자 순서) -->
            <div class="card">
                <div class="card-h">
                    <span class="card-t">챗별</span>
                    <span class="card-s">{ENV_INFO[env].label} · 비용 큰 순</span>
                </div>
                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th class="l">챗</th>
                                <th>턴</th>
                                <th>학습자</th>
                                <th>LLM</th>
                                <th>음성</th>
                                <th>합계</th>
                                <th>턴당</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each rows as r (r.chatId)}
                                <tr class:zero={r.b.costUsd === 0 && r.b.turns === 0}>
                                    <td class="l title" title={r.title}>{r.title}</td>
                                    <td>{int(r.b.turns)}</td>
                                    <td>{int(r.b.learners)}</td>
                                    <td>
                                        {krw(r.b.llm.costUsd)}
                                        <div class="sub">{int(r.b.llm.calls)}회 · {compact(r.b.llm.inputTokens + r.b.llm.outputTokens)} 토큰</div>
                                    </td>
                                    <td>
                                        {krw(r.b.tts.costUsd + r.b.stt.costUsd)}
                                        {#if voiceLine(r.b)}<div class="sub">{voiceLine(r.b)}</div>{/if}
                                    </td>
                                    <td class="strong">{krw(r.b.costUsd)}</td>
                                    <td>{per(r.b.costUsd, r.b.turns)}</td>
                                </tr>
                            {/each}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td class="l">합계</td>
                                <td>{int(cur.turns)}</td>
                                <td title="여러 챗을 쓴 학습자는 한 번만 셉니다">{int(cur.learners)}</td>
                                <td>{krw(cur.llm.costUsd)}</td>
                                <td>{krw(cur.tts.costUsd + cur.stt.costUsd)}</td>
                                <td class="strong">{krw(cur.costUsd)}</td>
                                <td>{per(cur.costUsd, cur.turns)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <!-- LLM 분해 — 어디에 돈이 드는지(용도) / 어떤 모델이 드는지 -->
            <div class="split">
                <div class="card">
                    <div class="card-h"><span class="card-t">LLM 용도별</span><span class="card-s">{ENV_INFO[env].label}</span></div>
                    {#if purposes.length}
                        <ul class="bars">
                            {#each purposes as p (p.key)}
                                <li>
                                    <div class="bar-h"><span>{p.label}</span><span class="num">{krw(p.costUsd)}</span></div>
                                    <div class="bar"><i style="width:{pct(p.costUsd, maxPurpose)}%"></i></div>
                                    <div class="sub">{int(p.calls)}회 · 입력 {compact(p.inputTokens)}{#if p.cachedTokens}(캐시 {compact(p.cachedTokens)}){/if} · 출력 {compact(p.outputTokens)}</div>
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <div class="none">LLM 기록이 없어요.</div>
                    {/if}
                </div>
                <div class="card">
                    <div class="card-h"><span class="card-t">LLM 모델별</span><span class="card-s">{ENV_INFO[env].label}</span></div>
                    {#if models.length}
                        <ul class="bars">
                            {#each models as m (m.name)}
                                <li>
                                    <div class="bar-h"><span class="mono">{m.name}</span><span class="num">{m.unpriced ? "요율 없음" : krw(m.costUsd)}</span></div>
                                    <div class="bar"><i style="width:{pct(m.costUsd, maxModel)}%"></i></div>
                                    <div class="sub">{int(m.calls)}회 · 입력 {compact(m.inputTokens)} · 출력 {compact(m.outputTokens)}</div>
                                </li>
                            {/each}
                        </ul>
                    {:else}
                        <div class="none">LLM 기록이 없어요.</div>
                    {/if}
                </div>
            </div>
        {/if}

        <p class="foot">
            금액은 각 사 공개 요금표로 계산한 <b>추정치</b>예요(할인·약정 미반영, 환율 ₩{int(rate)}/$ 고정). 실제 청구서와 다를 수 있어요.
            읽어주기는 같은 문장을 처음 만들 때만 비용이 들고, 이후에는 저장해 둔 음성을 다시 써요.
            LLM 기록은 사용량 기록 기능을 배포한 뒤부터 쌓여요.
        </p>
    {/if}
</div>

<style>
    .usage {
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .head {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }
    .head-l {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .head h3 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: hsl(var(--sb-foreground));
    }
    .pill {
        border-radius: 999px;
        background: hsl(var(--sb-muted));
        padding: 2px 8px;
        font-size: 12px;
        color: hsl(var(--sb-muted-foreground));
    }
    .seg {
        display: inline-flex;
        flex-wrap: wrap;
        padding: 2px;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 8px;
        background: hsl(var(--sb-muted) / 0.4);
    }
    .seg button {
        border: none;
        background: transparent;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 600;
        color: hsl(var(--sb-muted-foreground));
        border-radius: 6px;
        cursor: pointer;
    }
    .seg button.on {
        background: hsl(var(--sb-card));
        color: hsl(var(--sb-primary));
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
    }
    .custom {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 6px;
        font-size: 12px;
        color: hsl(var(--sb-muted-foreground));
    }
    .custom input {
        border: 1px solid hsl(var(--sb-border));
        border-radius: 6px;
        padding: 4px 8px;
        font-size: 12px;
        background: hsl(var(--sb-card));
        color: hsl(var(--sb-foreground));
    }
    .period {
        font-size: 12px;
        color: hsl(var(--sb-muted-foreground));
    }
    .dim {
        color: hsl(var(--sb-muted-foreground));
    }
    .empty {
        padding: 36px 16px;
        text-align: center;
        font-size: 13px;
        color: hsl(var(--sb-muted-foreground));
        border: 1px dashed hsl(var(--sb-border));
        border-radius: 8px;
        background: hsl(var(--sb-muted) / 0.3);
    }
    .empty.err {
        color: hsl(var(--sb-destructive, 0 72% 51%));
    }

    /* 환경 카드 */
    .envs {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
    }
    .env {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
        text-align: left;
        border: 1px solid hsl(var(--sb-border));
        border-radius: 10px;
        padding: 12px 14px;
        background: hsl(var(--sb-card));
        color: hsl(var(--sb-foreground));
        cursor: pointer;
        outline: none;
    }
    .env:focus-visible {
        box-shadow: 0 0 0 2px hsl(var(--sb-primary) / 0.5);
    }
    .env.on {
        border-color: hsl(var(--sb-primary));
        box-shadow: 0 0 0 1px hsl(var(--sb-primary));
    }
    .env-h {
        display: flex;
        align-items: baseline;
        gap: 6px;
        flex-wrap: wrap;
    }
    .env-t {
        font-size: 13px;
        font-weight: 700;
    }
    .env.on .env-t {
        color: hsl(var(--sb-primary));
    }
    .env-d {
        font-size: 11px;
        color: hsl(var(--sb-muted-foreground));
    }
    .env-n {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.15;
        font-variant-numeric: tabular-nums;
    }
    .env-usd {
        margin-left: 6px;
        font-size: 12px;
        font-weight: 500;
        color: hsl(var(--sb-muted-foreground));
    }
    .env-rows {
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-size: 12px;
    }
    .env-rows > div {
        display: flex;
        gap: 8px;
        min-width: 0;
    }
    .env-rows dt {
        flex: 0 0 30px;
        color: hsl(var(--sb-muted-foreground));
    }
    .env-rows dd {
        margin: 0;
        min-width: 0;
        overflow-wrap: anywhere;
        font-variant-numeric: tabular-nums;
    }
    .note {
        font-size: 12px;
        line-height: 1.5;
        padding: 8px 10px;
        border-radius: 8px;
        background: hsl(var(--sb-muted) / 0.5);
        color: hsl(var(--sb-muted-foreground));
    }
    .note.warn {
        background: hsl(38 92% 50% / 0.12);
        border-left: 3px solid hsl(38 92% 50%);
        color: hsl(var(--sb-foreground));
    }

    /* 카드 · 표 */
    .card {
        border: 1px solid hsl(var(--sb-border));
        border-radius: 8px;
        padding: 12px;
        background: hsl(var(--sb-card));
        min-width: 0;
    }
    .card-h {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-bottom: 8px;
    }
    .card-t {
        font-size: 13px;
        font-weight: 600;
        color: hsl(var(--sb-foreground));
    }
    .card-s {
        font-size: 11px;
        color: hsl(var(--sb-muted-foreground));
    }
    .table-wrap {
        overflow-x: auto;
    }
    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
        font-variant-numeric: tabular-nums;
    }
    th,
    td {
        padding: 7px 8px;
        text-align: right;
        white-space: nowrap;
        border-bottom: 1px solid hsl(var(--sb-border));
        vertical-align: top;
    }
    th {
        font-weight: 600;
        font-size: 11px;
        color: hsl(var(--sb-muted-foreground));
    }
    th.l,
    td.l {
        text-align: left;
    }
    td.title {
        max-width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    td.strong {
        font-weight: 700;
    }
    tr.zero td {
        color: hsl(var(--sb-muted-foreground));
        opacity: 0.7;
    }
    tfoot td {
        font-weight: 600;
        border-bottom: none;
        border-top: 1px solid hsl(var(--sb-border));
    }
    .sub {
        margin-top: 2px;
        font-size: 10.5px;
        color: hsl(var(--sb-muted-foreground));
        white-space: nowrap;
    }

    /* 분해 막대 */
    .split {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
    }
    .bars {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    .bar-h {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        font-size: 12px;
    }
    .bar-h span:first-child {
        min-width: 0;
        overflow-wrap: anywhere;
    }
    .num {
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }
    .mono {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 11.5px;
    }
    .bar {
        margin-top: 4px;
        height: 6px;
        border-radius: 999px;
        background: hsl(var(--sb-muted));
        overflow: hidden;
    }
    .bar i {
        display: block;
        height: 100%;
        border-radius: 999px;
        background: hsl(var(--sb-primary));
    }
    .bars .sub {
        white-space: normal;
    }
    .none {
        font-size: 12px;
        color: hsl(var(--sb-muted-foreground));
    }
    .foot {
        margin: 0;
        font-size: 11.5px;
        line-height: 1.6;
        color: hsl(var(--sb-muted-foreground));
    }
    @media (max-width: 720px) {
        .envs,
        .split {
            grid-template-columns: 1fr;
        }
    }
</style>
